// EST-CE QUE CE FOURNISSEUR EST RELEVÉ, ET SINON POURQUOI — la règle, écrite UNE fois.
//
// ⚠️ 🔴 ELLE EXISTE PARCE QUE DEUX ÉCRANS LA POSAIENT DIFFÉREMMENT. La vue d'exploitation concluait « pas
// relevé » depuis l'ABSENCE dans la réponse du serveur ; la fiche fournisseur la recalculait de son côté
// (« actif ET au moins un compte actif »). Les deux s'accordaient le jour où elles ont été écrites — et
// c'est exactement ainsi qu'une divergence commence : le jour où le serveur ajoute une condition, un écran
// la suit et l'autre annonce une heure de relève qui n'a pas lieu.
//
// ⚠️ LE SERVEUR TRANCHE, L'ÉCRAN EXPLIQUE. C'est la seule répartition tenable : la liste des rythmes
// effectifs ne contient QUE les fournisseurs relevables, donc la PRÉSENCE est la vérité. Ce que l'écran
// ajoute est le POURQUOI d'une absence, qu'il déduit de ce qu'il a sous la main — et il ne dit que ce
// qu'il sait.
import type { ApiEffectiveRhythm } from './api';

/** Ce qu'un fournisseur a le droit d'afficher à propos de sa relève. */
export type FetchState =
  | { scheduled: true; rhythm: ApiEffectiveRhythm }
  | { scheduled: false; why: 'supplier-paused' | 'no-account' | 'no-rhythm' | 'rhythm-paused' | 'rhythm-dead' | 'unknown' };

export interface SupplierFetchFacts {
  /** Le fournisseur lui-même est-il actif ? */
  active: boolean;
  /** Combien de comptes de transport il porte, et combien sont actifs. */
  accounts: number;
  activeAccounts: number;
  /** Son rythme est-il EN SERVICE ? (`schedule.active`)
   *  ⚠️ METTRE DE CÔTÉ N'EST PAS SUPPRIMER : l'heure reste en base, donc la reprise rend le même rythme.
   *  Confondre les deux à l'écran ferait croire qu'il faut la ressaisir. */
  rhythmActive: boolean;
  /**
   * Porte-t-il un rythme ENREGISTRÉ ?
   * ⚠️ 🔴 DEPUIS LE RETRAIT DU RYTHME COMMUN, « sans rythme » VEUT DIRE « pas relevé » : il n'existe plus
   * de défaut où retomber. C'est désormais la cause la plus FRÉQUENTE d'une non-relève — tout fournisseur
   * créé naît ainsi — et la distinguer d'un rythme CASSÉ compte : l'un se remplit, l'autre se corrige.
   */
  hasRhythm: boolean;
}

/**
 * Le verdict d'affichage pour UN fournisseur.
 *
 * ⚠️ `rhythm` PRÉSENT ⇒ RELEVÉ, sans condition supplémentaire. Ajouter ici un « et si les comptes sont
 * actifs » ferait de l'écran un second juge, qui contredirait le serveur au premier désaccord — or c'est
 * le serveur qui pose les crons.
 * ⚠️ `'unknown'` N'EST PAS UN DÉFAUT DE PLUS, C'EST L'HONNÊTETÉ : si un fournisseur actif, pourvu de
 * comptes actifs, est malgré tout absent de la liste, l'écran ne doit pas inventer une explication
 * plausible — il dit qu'il ne sait pas. Une raison fausse se corrige moins vite qu'une raison absente.
 */
export function fetchStateOf(rhythm: ApiEffectiveRhythm | undefined | null, facts: SupplierFetchFacts): FetchState {
  if (rhythm) return { scheduled: true, rhythm };
  if (!facts.active) return { scheduled: false, why: 'supplier-paused' };
  if (facts.activeAccounts === 0) return { scheduled: false, why: 'no-account' };
  if (!facts.hasRhythm) return { scheduled: false, why: 'no-rhythm' };
  // ⚠️ MIS DE CÔTÉ SE DIT AVANT « NE DÉCLENCHE JAMAIS » : un rythme suspendu n'est pas cassé, et l'envoyer
  //    corriger une expression saine serait une fausse piste.
  if (!facts.rhythmActive) return { scheduled: false, why: 'rhythm-paused' };
  // ⚠️ IL A TOUT CE QU'IL FAUT ET N'EST PAS RELEVÉ : son rythme est enregistré mais ne déclenche jamais.
  //    C'est le cas le plus traître — l'écran montre une configuration, et rien ne part.
  return { scheduled: false, why: 'rhythm-dead' };
}

/** La phrase montrée à l'exploitant pour chaque cause — une par cause, aucune formule attrape-tout. */
export const WHY_TEXT: Record<Extract<FetchState, { scheduled: false }>['why'], string> = {
  'supplier-paused': 'Ce fournisseur est en pause : aucune relève n’a lieu, quel que soit le rythme enregistré.',
  'no-account': 'Aucun compte de transport actif : il n’y a nulle part où aller chercher les fichiers.',
  'no-rhythm': 'Aucun rythme enregistré : ce fournisseur n’est pas relevé. Donnez-lui son heure ci-dessous.',
  'rhythm-paused': 'Son rythme est mis de côté : la relève est suspendue, mais son heure est conservée.',
  'rhythm-dead': 'Son rythme est enregistré mais ne déclenche jamais — corrigez-le ci-dessous, sinon rien n’arrivera.',
  'unknown': 'Le serveur ne le relève pas, et la raison n’est pas lisible depuis cet écran.',
};
